import { NADAAccessory } from 'api/vehicles/postNADAAccessories'
import axios, { AxiosError } from 'axios'
import config from 'config'
import { OctaneLogger } from 'integrations/datadog/OctaneLogger'
import AssetType from 'state/enums/AssetType'
import RvType from 'state/enums/RvType'

import { defaultLosJsonHeaders } from '../requestHeaders'

interface AssetBase {
    collateral: number
    condition: string
    id: number
    make: string
    model: string
    year: number
}

export interface AssetAutomobile extends AssetBase {
    automobile_id: number | null
    automobile_type: string
    booking_source: string | null
    color: string | null
    invoice_price: number | null
    is_cpo?: boolean | null
    like_invoice_price: number | null
    make_id: number | null
    mileage: number | null
    model_id: number | null
    nada_accessories?: NADAAccessory[] | null
    oem: string | null
    trim: string
    vehicle: undefined
    wholesale_price: number | null
}

export interface AssetMarineEngine extends AssetBase {
    engine_type: string | null
    fuel_type: string | null
    horsepower: number
    hours: number | null
    invoice_price: number | null
    mileage: undefined
    vehicle: undefined
}

export interface AssetMarineHull extends AssetBase {
    boat_type: string | null
    hours: number | null
    invoice_price: number | null
    length_ft: number
    mileage: undefined
    vehicle: undefined
}

export interface AssetMarineTrailer extends AssetBase {
    description: string
    mileage: undefined
    name: string
    sales_price: number | null
    vehicle: undefined
    weight: number | null
}

export interface AssetPowersports extends AssetBase {
    mileage: number | null
    vehicle: number
    vehicle_type: string
}

export interface AssetRv extends AssetBase {
    invoice_price: number | null
    make_id: string | null
    manufacturer_price: number
    mileage: number | null
    model_id: string | null
    oem: string | null
    retail_price: number | null
    rv: number
    rv_type: RvType | null
    vehicle: undefined
    wholesale_price: number
    writein_price: number | null
}

export interface AssetOpe extends AssetBase {
    data_source: string | null
    equipment_type: string | null
    equipment_sub_type: string | null
    msrp: number | null
    oem?: string | null
    outdoor_power_equipment_id: number | null
    serial_number: string | null
    sku: string | null
    vehicle: undefined
}

export interface CollateralsAssets<Asset> {
    application: number
    asset: Asset
    asset_name: string
    asset_sales_price: string | null
    asset_subtype: string | null
    asset_type: AssetType
    asset_valuation_method: string | null
    asset_value: number | null
    asset_value_label: string | null
    data_source: string
    id: number
    is_auto_generated: boolean
    unique_external_id: string | null
}

export type CollateralsAssetsResponse = CollateralsAssets<
    | AssetAutomobile
    | AssetMarineEngine
    | AssetMarineHull
    | AssetMarineTrailer
    | AssetPowersports
    | AssetRv
    | AssetOpe
>

export default async function getCollateralsAssets(
    applicationUuid: string,
    abortController?: AbortController,
): Promise<CollateralsAssetsResponse[]> {
    try {
        if (!applicationUuid) {
            return []
        }

        const { data } = await axios.get(
            `${config.API_BASE_URL}/api/v3/applications/${applicationUuid}/collaterals-assets/`,
            {
                signal: abortController?.signal,
                withCredentials: true,
                headers: defaultLosJsonHeaders(),
            },
        )

        return data
    } catch (error) {
        const axiosError = error as AxiosError

        OctaneLogger.axiosError(
            `Failed to get collateral assets: ${axiosError.message}`,
            axiosError,
            { applicationUuid },
        )

        throw axiosError
    }
}
